import React from "react";
import { type GetStaticProps, type NextPage } from "next";
import Image from "next/legacy/image";
import {
  type Banner,
  getAnnouncementBanner,
  getPage,
  getSimpleIntegrations,
  type IntegrationSimple,
  type Page as SanityPage,
} from "lib/sanity";
import { Box, SimpleGrid, Text, Flex } from "@chakra-ui/react";
import { Page } from "components/page";
import { HalfHeroAnimation, HeroVideo } from "components/half-hero-animation";
import { Customers } from "components/logos";
import { Section } from "components/section";
import { Quote } from "components/quotes";
import { AnimatedAccordion } from "components/animated-accordion";
import { Feature } from "components/feature";
import DestinationMarquee from "components/destination-marquee";
import { CdpLeader } from "components/cdp-leader";
import { getPageMetaImage } from "utils";
import { integrationsCount } from "../../constants/integrations";
import fullWidthImage from "../../../public/assets/platform/identity-resolution/full-width.png";
import fullWidthMobileImage from "../../../public/assets/platform/identity-resolution/full-width-mobile.png";
import accordionImage1 from "../../../public/assets/platform/identity-resolution/accordion-1.svg";
import accordionImage2 from "../../../public/assets/platform/identity-resolution/accordion-2.svg";
import accordionImage3 from "../../../public/assets/platform/identity-resolution/accordion-3.png";

type PageProps = {
  page: SanityPage;
  announcementBanner?: Banner;
  destinations: IntegrationSimple[];
};

const IdentityResolution: NextPage<Readonly<PageProps>> = ({ page, announcementBanner, destinations }) => {
  return (
    <Page
      title={page.metaTitle}
      description={page.metaDescription}
      image={getPageMetaImage({ src: page.metaImageUrl, alt: page.metaTitle })}
      announcementBanner={announcementBanner}
    >
      <HalfHeroAnimation
        hideSecondary
        eyebrow={page.eyebrow}
        title={page.title}
        description={page.description}
        animationElement={
          <HeroVideo
            title="Hightouch Identity Resolution"
            description={page.metaDescription}
            poster="https://cdn.sanity.io/images/pwmfmi47/production/2b8cadf51e33500b663ea44e73cbfda6070a4e1f-1086x940.webp"
            uploadDate="2023-07-09T08:00:00+08:00"
            sourceMp4="https://cdn.sanity.io/files/pwmfmi47/production/3e5c582e5f6d4844bd9610cb44c05b2e7048ef2c.mp4"
          />
        }
      />

      {page.customers && (
        <Customers
          customers={page.customers}
          title="The world's most innovative companies choose Hightouch as their Composable CDP"
          titleMaxW="2xl"
          mt={{ base: 16, md: 24 }}
        />
      )}

      <Section center bg="infographicGradient" color="white">
        <Box textAlign="center" maxW="3xl" mx="auto">
          <Text as="h2" textStyle="h4">
            Resolve identities in your warehouse - not in a black box
          </Text>

          <Text fontSize="lg" mt={5}>
            Traditional Customer Data Platforms perform identity resolution within their own rigid systems. You don’t
            have control, and you have to store your data with them. Instead, Hightouch unifies customer profiles
            directly in your data warehouse.
          </Text>
        </Box>

        <Box display={{ base: "none", md: "block" }} pos="relative" w="90%" mt={{ base: 12, md: 16, lg: 20 }}>
          <Image
            src={fullWidthImage}
            alt="Hightouch unifies and activates customer profiles directly from your data warehouse with identity resolution."
            width={1200}
            height={500}
          />
        </Box>

        <Flex
          display={{ base: "flex", md: "none" }}
          pos="relative"
          align="center"
          justify="center"
          w="70%"
          maxW="300px"
          mx="auto"
          mt={{ base: 16, md: 24 }}
        >
          <Image
            src={fullWidthMobileImage}
            alt="Hightouch unifies and activates customer profiles directly from your data warehouse with identity resolution."
            width={265}
            height={1627}
          />
        </Flex>
      </Section>

      <Section>
        <Quote
          author="Rachel Bradley-Haas"
          company="Big Time Data"
          job="Co-Founder & CEO"
          quote="Hightouch is empowering companies to self-serve identity and entity resolution, without writing code. This is a game-changer and puts Hightouch in a league of its own for data management platforms."
          authorImage="https://cdn.sanity.io/images/pwmfmi47/production/cb1273c3686b68ffbb474e4ed393a7e37870864b-776x720.png"
          logo="https://cdn.sanity.io/images/pwmfmi47/production/cf1411ab20b2c4730fe08f57f7596798a8342cc3-356x141.png"
        />
      </Section>

      <Section>
        <Text as="h2" textStyle="h4" textAlign={{ base: "center", md: "left" }}>
          How it works
        </Text>

        <AnimatedAccordion
          mt={{ base: 8, md: 12 }}
          align="right"
          items={[
            {
              image: accordionImage1,
              imageAlt: "Click events, transactions, users, and other data stored in a data warehouse.",
              label: "Connect your data warehouse",
              content: "Hightouch performs identity resolution inside your data warehouse.",
            },
            {
              image: accordionImage2,
              imageAlt: "Hightouch's no-code SQL builder.",
              label: "Define merge rules",
              content:
                "Define exact or fuzzy-match sets of rules to unify profiles. Perform custom identity resolution, tailored to your use-cases, without writing a line of code. ",
            },
            {
              image: accordionImage3,
              imageAlt: "User data syncing from Snowflake to Salesforce with Hightouch.",
              label: "Activate profiles",
              content: `Turn insights into actions. Hightouch can activate customer data to ${integrationsCount}+ destinations.`,
            },
          ]}
        />
      </Section>

      <Section>
        <Text as="h2" textStyle="h4" textAlign={{ base: "center", md: "left" }}>
          Identity resolution tailored to your business
        </Text>

        <Text fontSize="lg" mt={5} textAlign={{ base: "center", md: "left" }}>
          You bring the data. We’ll create the unified customer profiles.
        </Text>

        <SimpleGrid columns={{ sm: 1, md: 3, lg: 3 }} gap={16} mt={{ base: 8, md: 16 }}>
          <Feature
            image="/assets/platform/identity-resolution/feature-1.svg"
            imageWidth="54px"
            title="Comprehensive"
            description="Create accurate customer profiles with all your data, no matter where it’s stored. You can even unify profiles for non-person entities, such as stores, stock units, households and more."
          />

          <Feature
            image="/assets/platform/identity-resolution/feature-2.png"
            imageWidth="54px"
            title="Flexible"
            description="Build, test, and tune your own identity resolution rules without touching code. Make changes when you want to, roll them back if you need to."
          />

          <Feature
            image="/assets/platform/identity-resolution/feature-3.svg"
            imageWidth="54px"
            title="Secure"
            description="Stop sending data outside of your infrastructure. Build profiles directly within your data warehouse to ensure compliance for even the most sensitive data sets."
          />
        </SimpleGrid>
      </Section>

      <CdpLeader />

      <DestinationMarquee destinations={destinations} />
    </Page>
  );
};

export default IdentityResolution;

export const getStaticProps: GetStaticProps<PageProps> = async () => {
  const [page, announcementBanner, destinations] = await Promise.all([
    getPage("/platform/identity-resolution"),
    getAnnouncementBanner(),
    getSimpleIntegrations("destination"),
  ]);

  if (!page) {
    return {
      notFound: true,
    };
  }

  return {
    props: {
      page,
      announcementBanner,
      destinations,
    },
    revalidate: 60,
  };
};
